import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Divider,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import SurgicalIcon from '@mui/icons-material/MedicalServices';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SurgicalProcedurePanel from './SurgicalProcedurePanel';

interface SurgicalProcedureIntegrationDemoProps {
  encounterId: string;
  patientId: string;
  fin?: string;
  /** 
   * Show the dismissible integration example section.
   * Useful for demos, training, or first-time users.
   * Defaults to false in production usage.
   */
  showIntegrationExample?: boolean;
}

// Mock data for demonstration
const mockPatient = {
  id: 'patient-123',
  name: 'John Smith',
  mrn: 'MRN12345'
};

const mockSurgicalProcedures = [
  {
    id: 'surg-001',
    procedureType: 'Laparoscopic Cholecystectomy',
    specialty: 'General Surgery',
    status: 'ordered',
    orderDate: new Date('2024-01-15T10:30:00'),
    estimatedDuration: 90,
    requiresConsent: true,
    specialInstructions: 'NPO after midnight. Pre-op antibiotics required.',
    orderingProvider: 'Dr. Sarah Johnson, MD',
    priority: 'Routine'
  },
  {
    id: 'surg-002',
    procedureType: 'Appendectomy',
    specialty: 'General Surgery',
    status: 'completed',
    orderDate: new Date('2024-01-14T14:00:00'),
    estimatedDuration: 45,
    requiresConsent: true,
    specialInstructions: 'Emergency procedure',
    orderingProvider: 'Dr. Michael Chen, MD',
    priority: 'STAT'
  }
];

const SurgicalProcedureIntegrationDemo: React.FC<SurgicalProcedureIntegrationDemoProps> = ({
  encounterId,
  patientId,
  fin,
  showIntegrationExample = false
}) => {
  const [selectedProcedure, setSelectedProcedure] = useState<any>(null);
  const [showProcedurePanel, setShowProcedurePanel] = useState(false);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showIntegrationNote, setShowIntegrationNote] = useState(showIntegrationExample);

  const handleViewProcedure = (procedure: any) => {
    setSelectedProcedure(procedure);
    setShowProcedurePanel(true);
  };

  const handleCloseProcedurePanel = () => {
    setShowProcedurePanel(false);
    setSelectedProcedure(null);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'warning';
      case 'ordered':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'stat':
        return 'error';
      case 'urgent':
        return 'warning';
      case 'routine':
        return 'info';
      default:
        return 'default';
    }
  };

  if (showProcedurePanel && selectedProcedure) {
    return (
      <SurgicalProcedurePanel
        surgicalProcedure={selectedProcedure}
        patient={mockPatient}
        encounterId={encounterId}
        fin={fin}
        onBack={handleCloseProcedurePanel}
        showBackButton={true}
        embedded={false}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>


      {/* Integration Note - Now Dismissible */}
      {showIntegrationNote && (
        <Card sx={{ mb: 3, borderRadius: 2, backgroundColor: '#e3f2fd', position: 'relative' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                  Integration Example
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This demonstrates how surgical procedures can be integrated into existing encounters (like inpatient admissions) 
                  rather than creating separate surgical encounters. The surgical procedure panel provides the same rich UI/UX 
                  as the standalone surgical encounter but works within the context of the parent encounter.
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => setShowIntegrationNote(false)}
                sx={{ color: '#1976d2', ml: 1 }}
                aria-label="Dismiss integration example"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      )}



      {/* Surgical Procedures List */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Surgical Procedures ({mockSurgicalProcedures.length})
      </Typography>

      <Stack spacing={2}>
        {mockSurgicalProcedures.map((procedure) => (
          <Card key={procedure.id} sx={{ borderRadius: 2, '&:hover': { boxShadow: 4 } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {procedure.procedureType}
                    </Typography>
                    <Chip
                      label={procedure.status}
                      color={getStatusColor(procedure.status) as any}
                      size="small"
                    />
                    <Chip
                      label={procedure.priority}
                      color={getPriorityColor(procedure.priority) as any}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {procedure.specialty} • {procedure.orderingProvider}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ordered: {procedure.orderDate.toLocaleDateString()} • Duration: {procedure.estimatedDuration} min
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleViewProcedure(procedure)}
                  sx={{ ml: 2 }}
                >
                  View Details
                </Button>
              </Box>
              
              {procedure.specialInstructions && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Special Instructions:
                    </Typography>
                    <Typography variant="body2">
                      {procedure.specialInstructions}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Empty State */}
      {mockSurgicalProcedures.length === 0 && (
        <Card sx={{ borderRadius: 2, textAlign: 'center', py: 4 }}>
          <CardContent>
            <SurgicalIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No Surgical Procedures
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              No surgical procedures have been ordered for this encounter.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setShowOrderDialog(true)}
            >
              Order First Procedure
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Order Dialog (Placeholder) */}
      <Dialog open={showOrderDialog} onClose={() => setShowOrderDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Order Surgical Procedure</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ py: 3 }}>
            This would integrate with your existing order/procedure system to allow ordering surgical procedures.
            The new surgical order type has been added to the system and can be selected in the procedure ordering interface.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOrderDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setShowOrderDialog(false)}>
            Open Order Interface
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SurgicalProcedureIntegrationDemo; 